import { useRef, useLayoutEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Heading } from "../../";
import { SectionBadge } from "pages/AIHumanity/index";

gsap.registerPlugin(ScrollTrigger);

const steps = [
    {
        num: "01",
        heading: "Understands emotion + behavior.",
        color: "#6ddcff",
    },
    {
        num: "02",
        heading: "Maintains persistent relationship state.",
        color: "#a78bfa",
    },
    {
        num: "03",
        heading: "Adapts behavior in real time.",
        color: "#7e60f8",
    },
];

// Simple mobile-native vertical layout.
// No pinning, no horizontal scroll — just clean scroll-triggered fade-ins
// that work reliably on both iOS and Android.
function GsapSection1() {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const cards = gsap.utils.toArray(".sol-mobile-card");

            cards.forEach((card) => {
                gsap.fromTo(
                    card,
                    { autoAlpha: 0, y: 36 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.7,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 88%",   // fires early so it feels natural on mobile
                            once: true,          // don't reverse on scroll-up
                        },
                    }
                );
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                padding: "80px 24px 100px",
                background: "#000",
                display: "flex",
                flexDirection: "column",
                gap: 0,
            }}
        >
            {/* Section label */}
            <div style={{ marginBottom: 48 }}>
                <SectionBadge>The Solution</SectionBadge>
            </div>

            {/* Step cards */}
            {steps.map(({ num, heading, color }, i) => (
                <div key={i}>
                    <div
                        className="sol-mobile-card"
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                            paddingBottom: 40,
                            opacity: 0,         // initial state for GSAP
                            visibility: "hidden",
                        }}
                    >
                        {/* Step number */}
                        <span style={{
                            fontFamily: "Manrope, sans-serif",
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.14em",
                            color: color,
                            opacity: 0.8,
                        }}>
                            {num} / 03
                        </span>

                        {/* Heading */}
                        <Heading
                            size="xl"
                            as="h2"
                            className="!text-gray-300"
                            style={{ lineHeight: "1.2", maxWidth: 340 }}
                        >
                            {heading}
                        </Heading>

                        {/* Accent line */}
                        <div style={{
                            width: 48,
                            height: 2,
                            borderRadius: 1,
                            background: `linear-gradient(90deg, ${color}, transparent)`,
                            marginTop: 4,
                        }} />
                    </div>

                    {/* Divider between cards — not after the last one */}
                    {i < steps.length - 1 && (
                        <div style={{
                            height: 1,
                            background: "rgba(255,255,255,0.06)",
                            marginBottom: 40,
                        }} />
                    )}
                </div>
            ))}
        </div>
    );
}

export default GsapSection1;
